import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Activity, Clock, Eye, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
    </div>
  );
}

export function AdminActivityLogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logFilter, setLogFilter] = useState({ action: 'all', search: '', period: 'all' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/activity-logs?limit=200`, { headers });
      setLogs(res.data || []);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  };

  const filteredLogs = logs.filter(log => {
    if (logFilter.action !== 'all' && log.action !== logFilter.action) return false;
    if (logFilter.search && !log.user_email?.toLowerCase().includes(logFilter.search.toLowerCase())) return false;
    if (logFilter.period === 'today') {
      const today = new Date().toDateString();
      if (new Date(log.timestamp).toDateString() !== today) return false;
    } else if (logFilter.period === 'week') {
      const weekAgo = new Date(Date.now() - 7 * 86400000);
      if (new Date(log.timestamp) < weekAgo) return false;
    } else if (logFilter.period === 'month') {
      const monthAgo = new Date(Date.now() - 30 * 86400000);
      if (new Date(log.timestamp) < monthAgo) return false;
    }
    return true;
  });

  const todayLogs = logs.filter(l => new Date(l.timestamp).toDateString() === new Date().toDateString());

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-[hsl(var(--primary))]" /></div>;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 border border-emerald-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Users size={16} className="text-emerald-400" /><span className="text-xs text-[hsl(var(--text-muted))]">Active Users Today</span></div>
          <p className="text-2xl font-bold text-white">{new Set(todayLogs.map(l => l.user_id)).size}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Activity size={16} className="text-blue-400" /><span className="text-xs text-[hsl(var(--text-muted))]">Total Actions Today</span></div>
          <p className="text-2xl font-bold text-white">{todayLogs.length}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Clock size={16} className="text-amber-400" /><span className="text-xs text-[hsl(var(--text-muted))]">Login Count Today</span></div>
          <p className="text-2xl font-bold text-white">{todayLogs.filter(l => l.action === 'LOGIN').length}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500/10 to-violet-500/5 border border-purple-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Eye size={16} className="text-purple-400" /><span className="text-xs text-[hsl(var(--text-muted))]">Most Visited Page</span></div>
          <p className="text-lg font-bold text-white truncate">{(() => {
            const pages = logs.filter(l => l.action === 'VIEW').map(l => l.resource);
            const counts = pages.reduce((acc, p) => { acc[p] = (acc[p] || 0) + 1; return acc; }, {});
            return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
          })()}</p>
        </div>
      </div>

      {/* User Stats & Page Analytics */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <SectionHeader icon={Users} title="User Activity Stats" subtitle="Login count & engagement per user" />
          <div className="p-4 max-h-64 overflow-y-auto">
            {(() => {
              const stats = {};
              logs.forEach(l => {
                if (!stats[l.user_email]) stats[l.user_email] = { email: l.user_email, logins: 0, actions: 0, lastSeen: null };
                if (l.action === 'LOGIN') stats[l.user_email].logins++;
                stats[l.user_email].actions++;
                if (!stats[l.user_email].lastSeen || new Date(l.timestamp) > new Date(stats[l.user_email].lastSeen)) stats[l.user_email].lastSeen = l.timestamp;
              });
              return Object.values(stats).sort((a, b) => b.actions - a.actions).slice(0, 10).map((u, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--surface-elevated))]">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#DC2626] flex items-center justify-center text-xs font-bold">{u.email?.charAt(0)?.toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{u.email}</p>
                    <div className="flex items-center gap-3 text-[10px] text-[hsl(var(--text-muted))]"><span>{u.logins} logins</span><span>{u.actions} actions</span></div>
                  </div>
                  <div className="text-[10px] text-[hsl(var(--text-muted))]">{u.lastSeen ? new Date(u.lastSeen).toLocaleDateString('en-MY') : '-'}</div>
                </div>
              ));
            })()}
          </div>
        </div>
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <SectionHeader icon={Eye} title="Page Analytics" subtitle="Most visited pages by team" />
          <div className="p-4 max-h-64 overflow-y-auto">
            {(() => {
              const pages = logs.filter(l => l.action === 'VIEW').map(l => l.resource);
              const counts = pages.reduce((acc, p) => { acc[p] = (acc[p] || 0) + 1; return acc; }, {});
              const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
              const max = sorted[0]?.[1] || 1;
              return sorted.slice(0, 10).map(([page, count], i) => (
                <div key={i} className="flex items-center gap-3 mb-3">
                  <div className="w-6 h-6 rounded bg-[hsl(var(--surface-elevated))] flex items-center justify-center text-[10px] font-bold text-[hsl(var(--text-muted))]">{i + 1}</div>
                  <div className="flex-1">
                    <p className="text-sm">{page}</p>
                    <div className="w-full h-1.5 bg-[hsl(var(--surface-elevated))] rounded-full mt-1"><div className="h-full bg-gradient-to-r from-[#E63946] to-[#F97316] rounded-full" style={{ width: `${(count / max) * 100}%` }} /></div>
                  </div>
                  <span className="text-sm font-medium text-[hsl(var(--text-muted))]">{count}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* Activity Log List */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <SectionHeader icon={Activity} title="Activity Logs" subtitle={`${filteredLogs.length} activities (Latest first)`}
          action={
            <div className="flex gap-2">
              <Select value={logFilter.period} onValueChange={(v) => setLogFilter({ ...logFilter, period: v })}>
                <SelectTrigger className="w-28 h-8 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"><SelectValue placeholder="Period" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Time</SelectItem><SelectItem value="today">Today</SelectItem><SelectItem value="week">This Week</SelectItem><SelectItem value="month">This Month</SelectItem></SelectContent>
              </Select>
              <Input placeholder="Search user..." value={logFilter.search} onChange={(e) => setLogFilter({ ...logFilter, search: e.target.value })} className="w-40 h-8 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" />
              <Select value={logFilter.action} onValueChange={(v) => setLogFilter({ ...logFilter, action: v })}>
                <SelectTrigger className="w-28 h-8 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Actions</SelectItem><SelectItem value="LOGIN">Login</SelectItem><SelectItem value="VIEW">View</SelectItem><SelectItem value="CREATE">Create</SelectItem><SelectItem value="UPDATE">Update</SelectItem><SelectItem value="DELETE">Delete</SelectItem></SelectContent>
              </Select>
            </div>
          }
        />
        <div className="p-4 max-h-[400px] overflow-y-auto">
          <div className="space-y-2">
            {[...filteredLogs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((log) => (
              <div key={log.id} className="flex items-center gap-4 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  log.action === 'LOGIN' ? 'bg-[#10B981]/20' : log.action === 'DELETE' ? 'bg-[#EF4444]/20' :
                  log.action === 'CREATE' ? 'bg-[#3B82F6]/20' : log.action === 'UPDATE' ? 'bg-[#F59E0B]/20' :
                  log.action === 'VIEW' ? 'bg-[#8B5CF6]/20' : 'bg-[#6B7280]/20'
                }`}>
                  <Activity size={14} className={
                    log.action === 'LOGIN' ? 'text-[#10B981]' : log.action === 'DELETE' ? 'text-[#EF4444]' :
                    log.action === 'CREATE' ? 'text-[#3B82F6]' : log.action === 'UPDATE' ? 'text-[#F59E0B]' :
                    log.action === 'VIEW' ? 'text-[#8B5CF6]' : 'text-[#6B7280]'
                  } />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2"><span className="text-sm text-white">{log.user_email}</span><Badge className="bg-[#6B7280]/20 text-[#9CA3AF] text-[10px]">{log.action}</Badge></div>
                  <p className="text-xs text-[#6B7280]">{log.resource} {log.details && `- ${log.details}`}</p>
                </div>
                <div className="text-xs text-[#6B7280]">{new Date(log.timestamp).toLocaleString('en-MY')}</div>
              </div>
            ))}
            {filteredLogs.length === 0 && <div className="text-center py-8 text-[#6B7280]"><Activity size={40} className="mx-auto mb-2 opacity-50" /><p>No activity found</p></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
